import React, { memo, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import '@src/components/style/virtuoso.scss';
import { LayoutColors } from '@src/core/theme';
import { Order } from '@src/core/buyerApiClient';
import { device } from '@src/common/breakpoints';
import { logoutBuyer } from '@src/features/auth';
import useTranslate from '@src/i18n/useTranslate';
import Collapse from '@src/components/ui/Collapse';
import { useAppDispatch, useAppSelector, useWidgetParam } from '@src/core/hooks';
import { BodyRegular, BodyRegularSemiBold, BodySmall } from '@src/fonts/typography/Typography';

import OrderList from './OrderList';
import { Container, Wrapper } from '../shared';
import { totalOrdersSelector, isLoadingOrdersSelector } from '@src/features/order';
import { track } from '@src/services/analytics';

type Props = {
  onOrderSelection: (order: Order) => void;
  onOrderCheckout: (order: Order) => void;
};

const MyAccount = (props: Props) => {
  const { onOrderSelection, onOrderCheckout } = props;
  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();
  const logOutRedirectUrl = useWidgetParam('logout-redirect-to');
  const totalSales = useAppSelector(totalOrdersSelector);
  const isLoading = useAppSelector(isLoadingOrdersSelector);
  const [isExpanded, setIsExpanded] = useState(true);

  const hasNoSales = useMemo(() => totalSales === 0, [totalSales, isLoading]);

  const handleLogOut = () => {
    dispatch(logoutBuyer(logOutRedirectUrl || '/'));
    track('accountLogout');
  };

  useEffect(() => {
    track('accountAccess');
  }, []);

  return (
    <Wrapper>
      <Container aria-label='my account data'>
        <h1>{i18n('account_myPurchases')}</h1>
        <Collapse
          title={
            <CollapseTitleContainer>
              <h2>{i18n('account_myPurchases')}</h2>
              {!hasNoSales ? <Total>{i18n('account_total_purchases') + ': ' + totalSales}</Total> : null}
            </CollapseTitleContainer>
          }
          isExpanded={hasNoSales ? false : isExpanded}
          onClick={() => setIsExpanded((state) => !state)}
          disabled={hasNoSales}
        >
          <OrderList onOrderSelection={onOrderSelection} onOrderCheckout={onOrderCheckout} />
        </Collapse>

        {hasNoSales ? (
          <>
            {isLoading ? (
              <LoadingContainer>
                <span>{i18n('base_loading')}</span>
              </LoadingContainer>
            ) : (
              <EmptyState>{i18n('account_empty_state')}</EmptyState>
            )}
          </>
        ) : null}
      </Container>
      <LogOutButton onClick={handleLogOut}>{i18n('account_log_out_button')}</LogOutButton>
    </Wrapper>
  );
};

export default memo(MyAccount);

const Total = styled.span`
  ${BodySmall}
  color: ${LayoutColors.blackMain};

  ${device.smallTablet} {
    ${BodyRegular};
  }
`;

const LogOutButton = styled.button`
  width: 100%;
  max-width: 710px;
  border: 1px solid ${LayoutColors.errorMain};
  border-radius: 4px;
  background: ${LayoutColors.whiteLight};

  display: flex;
  align-items: center;
  justify-content: center;

  ${BodyRegularSemiBold};
  color: ${LayoutColors.errorMain};

  margin: 0 auto;
  padding: 10px;
  cursor: pointer;
`;

const CollapseTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  h2 {
    ${BodyRegularSemiBold};
    color: ${LayoutColors.blackMain};
  }
`;

const EmptyState = styled.span`
  display: inline-block;
  width: 100%;
  ${BodyRegularSemiBold};
  text-align: center;
  color: ${LayoutColors.blackLight};
`;

const LoadingContainer = styled.div`
  padding-top: 16px;
  display: flex;
  justify-content: center;

  span {
    color: ${LayoutColors.blackMain};
  }
`;
